<template>
  <ion-app>
    <main-menu v-bind:disabled="mainMenuConfig.disabled" v-bind:items="mainMenuConfig.items"/>
    <ion-router-outlet id="main-content" ref="routerOutlet"/>
  </ion-app>
</template>

<script lang="ts">
import {IonApp, IonRouterOutlet, useBackButton} from '@ionic/vue'
import {defineComponent, inject, reactive} from 'vue'
import {mapActions, mapGetters} from 'vuex'
import {App, URLOpenListenerEvent} from '@capacitor/app'
import {pushNotificationServiceKey} from '@/service/PushNotificationService'
import {ROLE_DRIVER, ROLE_SITE_MANAGER} from '@/service/api/CurrentUserApi'
import {DeliveryNoteDetails} from '@/router/driver'
import {SiteManagerDeliveryNoteDetailsRoute} from '@/router/sitemanager'
import {Subscription} from '@/service/subscription/Subscription'
import {DeliveryNoteStatusChangedBean} from '@/service/model/DeliveryNoteStatusChangedBean'
import {PushNotificationEvent} from '@/service/model/PushNotificationEvent'
import MainMenu, {MainMenuItem} from '@/components/common/menu/MainMenu.vue'
import {mainMenuConfigKey} from '@/keys'
import {ComposableSubscription} from '@/service/subscription/ComposableSubscription'
import {registerToAppOpen} from '@/utils/AppOpenUtils'
import {registerToAppState} from '@/utils/AppStateUtils'
import {getWebAppUrl} from '@/utils/OSUtils'
import {OrderEventReceivedBean, orderEventReceivedSchema} from '@/service/model/OrderEventReceivedBean'
import {deliveryNoteStatusChangedSchema} from '@/service/model/DeliveryNoteStatusChangedBean'
import {SecondeVieNotificationBean, secondeVieNotificationSchema} from '@/service/model/SecondeVieNotificationBean'
import {SiteManagerSecondeVieRoute} from '@/router/sitemanager'

export interface MainMenuConfig {
  disabled: boolean
  items: Array<MainMenuItem>
}

export default defineComponent({
  name: 'App',
  data() {
    return {
      subscription: undefined as Subscription | undefined
    }
  },
  setup() {
    const mainMenuConfig = reactive<MainMenuConfig>({
      disabled: false,
      items: []
    })
    return {
      mainMenuConfig,
      pushNotificationService: inject(pushNotificationServiceKey)
    }
  },
  provide() {
    return {
      [mainMenuConfigKey]: this.mainMenuConfig
    }
  },
  async mounted() {
    await this.registerToPushNotifications()

    // Clear badge count on app open
    this.pushNotificationService.getBadgeService().clear()
      .catch(error => console.warn('Failed to clear badge on app open', error))

    // Fetch current user info if already logged in to sync device token
    const isLoggedIn = await this.isLoggedIn()
    const selectedRole = this.$store.state.currentUserModule?.selectedRole
    if (isLoggedIn && selectedRole) {
      // Small delay to let device token registration complete
      await new Promise(resolve => setTimeout(resolve, 500))
      this.fetchCurrentUser()
        .then(() => {
          const currentUser = this.$store.getters.currentUser
          if (currentUser && currentUser.validatedUser === false && this.$route.name !== 'AccountVerification') {
            this.$router.replace({name: 'AccountVerification'})
          }
        })
        .catch(error => console.error('Failed to fetch current user on startup', error))
    }

    // Clear badge count and send device token to backend on every app resume
    const appStateSubscription = await registerToAppState(appState => {
      if (appState.isActive) {
        this.pushNotificationService.getBadgeService().clear()
          .catch(error => console.warn('Failed to clear badge on app resume', error))
        this.syncDeviceToken().catch(error => console.error('Failed to sync device token on app resume', error))
      }
    })
    if (this.subscription instanceof ComposableSubscription) {
      this.subscription.add(appStateSubscription)
    } else {
      const subscriptions = new ComposableSubscription()
      if (this.subscription) {
        subscriptions.add(this.subscription)
      }
      subscriptions.add(appStateSubscription)
      this.subscription = subscriptions
    }

    // Register modules to store changes.
    // Required because modules cannot access context while reloading from store.
    this.loadRegisterForStoreChanges()
    this.unloadRegisterForStoreChanges()

    // Exit app if user cannot go back
    useBackButton(-1, this.onBackButton)

    // Check app version is deprecated.
    this.checkAppVersion()

    // Refresh application configuration
    this.refreshConfig()

    // Display result from last sync
    this.displayLastResult()

    // Remove loading style since we will load the app.
    document.body.classList.remove('app-loading')
  },
  unmounted() {
    this.subscription?.unregister()
  },
  methods: {
    ...mapActions(['isLoggedIn', 'fetchCurrentUser', 'syncDeviceToken']),
    ...mapActions('appVersion', ['checkAppVersion']),
    ...mapActions('config', ['refreshConfig']),
    ...mapActions('sync', ['displayLastResult']),
    ...mapActions('load', {loadRegisterForStoreChanges: 'registerForStoreChanges'}),
    ...mapActions('unload', {unloadRegisterForStoreChanges: 'registerForStoreChanges'}),
    async registerToPushNotifications() {
      const subscriptions = new ComposableSubscription()
      subscriptions.add(this.pushNotificationService.registerForEventAction({
        event: PushNotificationEvent.DELIVERY_NOTE_STATUS_CHANGED,
        callback: this.onDeliveryNoteStatusChangedAction.bind(this),
        schema: deliveryNoteStatusChangedSchema
      }))
      subscriptions.add(this.pushNotificationService.registerForEventAction({
        event: PushNotificationEvent.NEW_ORDER_RECEIVED,
        callback: this.onOrderReceivedNotificationAction.bind(this),
        schema: orderEventReceivedSchema
      }))
      subscriptions.add(this.pushNotificationService.registerForEventAction({
        event: PushNotificationEvent.SECONDE_VIE_NOTIFICATION,
        callback: this.onSecondeVieNotificationAction.bind(this),
        schema: secondeVieNotificationSchema
      }))
      // Attendre la Promise ici
      const appOpenSubscription = await registerToAppOpen(this.onAppOpen.bind(this))
      subscriptions.add(appOpenSubscription)
      this.subscription = subscriptions
      this.pushNotificationService.register()
    },
    onDeliveryNoteStatusChangedAction(event: string, data: DeliveryNoteStatusChangedBean) {
      const deliveryNoteId = data.delivery_note_id
      if (!deliveryNoteId) {
        return
      }

      let route: string | undefined = undefined
      switch (this.currentUser?.role) {
      case ROLE_DRIVER:
        route = DeliveryNoteDetails
        break
      case ROLE_SITE_MANAGER:
        route = SiteManagerDeliveryNoteDetailsRoute
        break
      default:
      }
      if (route) {
        this.$router.replace({
          name: route,
          params: {id: deliveryNoteId}
        })
      }
    },
    onOrderReceivedNotificationAction(event: string, data: OrderEventReceivedBean) {
      const orderId = data.order_id
      if (!orderId) {
        return
      }

      let route: string | undefined = undefined
      if (this.currentUser?.role == ROLE_SITE_MANAGER) {
        route = 'SiteManagerOrder'
      }
      if (route) {
        this.$router.replace({
          name: route,
          params: {id: orderId}
        })
      }
    },
    onSecondeVieNotificationAction(event: string, data: SecondeVieNotificationBean) {
      const redirectUrl = data.redirectUrl
      this.$router.replace({
        name: SiteManagerSecondeVieRoute,
        query: { ...(redirectUrl ? { redirectUrl } : {}), _t: Date.now().toString() }
      })
    },
    onAppOpen(event: URLOpenListenerEvent) {
      const webAppUrl = getWebAppUrl()
      if (!webAppUrl) {
        return
      }
      if (!event.url.startsWith(webAppUrl)) {
        return
      }
      const path = event.url.substring(webAppUrl.length)
      const route = this.$router.resolve({path})
      if (route) {
        this.$router.replace(route)
      }
    },
    onBackButton(): void {
      const previousRoute = this.previousRoute
      if (previousRoute) {
        this.$router.replace(previousRoute)
      } else {
        App.exitApp()
      }
    }
  },
  computed: {
    ...mapGetters(['currentUser']),
    ...mapGetters('back', ['previousRoute'])
  },
  components: {
    MainMenu,
    IonApp, IonRouterOutlet
  }
})
</script>
